import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom'

import client from 'apollo-client'
import Checkbox from 'packages/ui/Checkbox'
import { useUser } from 'store'
import { useItemsSelected, useQueryVariables, useLastItemCheckedState } from '../store'
import { canEditRans } from '../permissions'
import OPERATION_FIND_RAN_ROOMS from '../operations-gql/queries'

const HeaderCheckbox = () => {
  const [user] = useUser()
  const { isStime } = useOutletContext()
  const [itemsSelected, setItemsSelected] = useItemsSelected()
  const queryVariables = useQueryVariables()
  
  const hasSelection = itemsSelected.length > 0

  if (!canEditRans({user, isStime})) {
    return null
  }

  return (
    <Checkbox
      checked={false}
      indeterminate={hasSelection}
      onChange={() => {
        if (hasSelection) {
          setItemsSelected([])
        } else {
          const { rooms } = client.readQuery({
            query: OPERATION_FIND_RAN_ROOMS,
            variables: queryVariables
          })
          setItemsSelected(rooms)
        }
      }}
    />
  )
}

const BodyCheckbox = ({ran, ctx}) => {
  const [user] = useUser()
  const { isStime } = useOutletContext()
  const [itemsSelected, setItemsSelected] = useItemsSelected()
  const [lastItemChecked, setLastItemChecked] = useLastItemCheckedState()
  
  const checked = useMemo(() => {
    return itemsSelected.findIndex(item => item.id === ran.id) > -1
  }, [itemsSelected, ran])
  
  const handleMultiSelect = () => {
    const fromIndex = ctx.items.findIndex(item => item.id === lastItemChecked.id)
    const toIndex = ctx.items.findIndex(item => item.id === ran.id)
    const start = Math.min(toIndex, fromIndex)
    const end = Math.max(toIndex, fromIndex)
    const newItems = []
    for (let i = start; i <= end; i++) {
      newItems.push(ctx.items[i])
    }
    setLastItemChecked(null)
    setItemsSelected(prev => {
      const prevIds = prev.map(({id}) => id)
      return [...prev, ...newItems.filter(item => !prevIds.includes(item.id))]
    })
  }
  
  const handleChange = (event) => {
    event.stopPropagation()
    
    if (event.nativeEvent.shiftKey && lastItemChecked !== null) {
      handleMultiSelect()
    } else {
      if (checked) {
        setItemsSelected(itemsSelected.filter(item => item.id !== ran.id))
      } else {
        setLastItemChecked(ran)
        setItemsSelected([...itemsSelected, ran])
      }
    }
  }

  if (!canEditRans({user, isStime})) {
    return null
  }

  return (
    <Checkbox
      checked={checked}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
    />
  )
}

const COLUMNS_CONFIG = [
  {
    name: 'check',
    header: {
      label: HeaderCheckbox,
    },
    style: {
      width: 24,
      paddingLeft: 8,
    },
    value: (ran, ctx) => <BodyCheckbox ran={ran} ctx={ctx} />,
  },
  {
    name: 'spaces',
    header: {
      label: ctx => ctx.t('ran.table.label')
    },
    value: ran => ran.name,
    sortBy: 'name',
    sortable: true
  },
  {
    name: 'wifi-key',
    header: {
      label: ctx => ctx.t('ran.table.wifi_keys')
    },
    value: ran => ran.wifi_psk
  },
  {
    name: 'ssid_name',
    header: {
      label: ctx => ctx.t('ran.table.ssid_col')
    },
    value: ran => ran.ssid_name || '-'
  },
  {
    name: 'vlan',
    header: {
      label: ctx => ctx.t('ran.table.vlan')
    },
    value: ran => ran.vlan || '-'
  },
  {
    name: 'creation-date',
    header: {
      label: ctx => ctx.t('ran.table.creation_date')
    },
    value: ran => new Date(ran.created_at).toLocaleDateString(),
    sortBy: 'created_at',
    sortable: true
  },
  {
    name: 'comments',
    header: {
      label: ctx => ctx.t('ran.table.description')
    },
    value: ran => ran.description,
    sortBy: 'description',
    sortable: true
  }
]

export default COLUMNS_CONFIG
